/**
 * Autoshops Voice sellable SKUs (TASK-769).
 * Retail is suggested; Admin V2 may override via subscription_plans later.
 * Do not put minutes on Base. Text Chat is never billed via sms:true.
 */

export const VOICE_BASE_PLAN = "addon_ai_voice";
export const VOICE_CALENDAR_PLAN = "addon_ai_voice_calendar";
export const VOICE_RECORDING_PLAN = "addon_ai_voice_recording";
export const VOICE_TEXT_CHAT_PLAN = "addon_ai_voice_text_chat";

export const VOICE_PLAN_NAMES = [
  VOICE_BASE_PLAN,
  VOICE_CALENDAR_PLAN,
  VOICE_RECORDING_PLAN,
  VOICE_TEXT_CHAT_PLAN,
];

/** Catalog metadata when DB row is missing or incomplete */
export const VOICE_SKU_CATALOG = [
  {
    name: VOICE_BASE_PLAN,
    displayName: "Voice Base",
    suggestedMonthly: 39,
    requiresBase: false,
    billable: true,
    blurb:
      "AI phone number, summaries and transcripts. Includes Autoshops.com calendar. No included minutes.",
  },
  {
    name: VOICE_CALENDAR_PLAN,
    displayName: "Calendar upgrade — Google / Outlook / CalDAV",
    suggestedMonthly: 25,
    requiresBase: true,
    billable: true,
    blurb: "Connect Google, Outlook, or CalDAV. Requires Voice Base.",
  },
  {
    name: VOICE_RECORDING_PLAN,
    displayName: "Call recording & transcript",
    suggestedMonthly: 25,
    requiresBase: true,
    billable: true,
    blurb: "Audio recording and playback (not included with Base).",
  },
  {
    name: VOICE_TEXT_CHAT_PLAN,
    displayName: "SMS Text Chat",
    suggestedMonthly: 15,
    requiresBase: true,
    billable: false,
    blurb: "Customers text your Voice number. Not billed yet.",
  },
];

export function catalogHasVoiceBase(addons = []) {
  return (addons || []).some((a) => a && a.name === VOICE_BASE_PLAN);
}

export function isVoicePlanName(name) {
  return typeof name === "string" && VOICE_PLAN_NAMES.includes(name);
}

export function isVoiceExtraName(name) {
  return isVoicePlanName(name) && name !== VOICE_BASE_PLAN;
}

export function voicePlansFromAddons(addons = []) {
  return VOICE_SKU_CATALOG.map((sku) => {
    const row = addons.find((a) => a.name === sku.name);
    return {
      ...sku,
      id: row?.id ?? null,
      monthly_price: row?.monthly_price ?? sku.suggestedMonthly,
      display_name: row?.display_name || sku.displayName,
      description: row?.description || sku.blurb,
      is_active: row ? row.is_active : sku.name === VOICE_BASE_PLAN ? 1 : 1,
      stripe_monthly_price_id: row?.stripe_monthly_price_id || null,
      fromDb: Boolean(row),
    };
  });
}

export function voiceSubtotalFromSelection(addons, selectedAddonIds) {
  const voice = voicePlansFromAddons(addons);
  let total = 0;
  for (const sku of voice) {
    if (!sku.id || !selectedAddonIds.includes(sku.id)) continue;
    if (!sku.billable) continue;
    total += parseFloat(sku.monthly_price) || 0;
  }
  return total;
}

/** When Base is off, drop all Voice extras from the selected id list */
export function cascadeVoiceSelection(addons, selectedAddonIds, nextSelectedIds) {
  const byId = new Map((addons || []).map((a) => [a.id, a]));
  const base = (addons || []).find((a) => a.name === VOICE_BASE_PLAN);
  if (!base) return nextSelectedIds;

  const hadBase = selectedAddonIds.includes(base.id);
  const hasBase = nextSelectedIds.includes(base.id);

  if (hadBase && !hasBase) {
    return nextSelectedIds.filter((id) => {
      const plan = byId.get(id);
      return !plan || !isVoicePlanName(plan.name);
    });
  }

  if (!hasBase) {
    return nextSelectedIds.filter((id) => {
      const plan = byId.get(id);
      return !plan || !isVoiceExtraName(plan.name);
    });
  }

  return nextSelectedIds;
}

export function canCheckoutVoicePlan(plan) {
  if (!plan) return false;
  if (plan.name === VOICE_TEXT_CHAT_PLAN) return false;
  if (!plan.billable) return false;
  return Boolean(plan.stripe_monthly_price_id);
}

/** Checkout: only billable Voice plans that have a Stripe price */
export function filterCheckoutPlanIds(addons, selectedAddonIds) {
  const byId = new Map((addons || []).map((a) => [a.id, a]));
  const skipped = [];
  const allowed = [];
  for (const id of selectedAddonIds) {
    const plan = byId.get(id);
    if (!plan) {
      allowed.push(id);
      continue;
    }
    if (!isVoicePlanName(plan.name)) {
      allowed.push(id);
      continue;
    }
    if (plan.name === VOICE_TEXT_CHAT_PLAN) {
      skipped.push({ id, reason: "Text Chat is not billed yet" });
      continue;
    }
    if (!plan.stripe_monthly_price_id) {
      skipped.push({
        id,
        reason: `${plan.display_name || plan.name} is not linked to Stripe yet`,
      });
      continue;
    }
    allowed.push(id);
  }
  return { allowed, skipped };
}
